import { catchError, map, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';

/**
 * services
 */
import { UnitApiService } from './api';

/**
 * interfaces
 */
import { ICreateUnitPayload, IUpdateUnitPayload } from '../interfaces';

/**
 * models
 */
import {
    IUnitDetailsModel, UnitDetailsModel,
    IUnitModel, UnitModel, IUnitChildModel, UnitChildModel,
    IUnitTreeModel, UnitTreeModel,
    IUnitListItemModel, UnitListItemModel,
} from '../models';

/**
 * Business / facade layer for Units operations.
 *
 * Unwraps {@link IResponse} envelopes, maps raw DTOs to view-models, and
 * exposes clean Observables. Consumers never see raw DTO shapes.
 */
@Injectable()
export class UnitService {

    /**
     * @param _api - HTTP layer for unit endpoints.
     */
    constructor(private readonly _api: UnitApiService) {}

    /**
     * Returns the first-level units (direct children of the root facility).
     * @returns Observable emitting an array of {@link IUnitModel}.
     */
    public getFirstLevel(): Observable<IUnitModel[]> {
        return this._api
            .getFirstLevel()
            .pipe(
                map((r) => r.data.map((dto) => new UnitModel(dto))),
            );
    }

    /**
     * Returns the direct children of a given unit for lazy tree loading.
     * @param parentId - ID of the parent unit.
     * @returns Observable emitting an array of {@link IUnitChildModel}.
     */
    public getChildren(parentId: string): Observable<IUnitChildModel[]> {
        return this._api
            .getChildren(parentId)
            .pipe(
                map((r) => r.data.map((dto) => new UnitChildModel(dto))),
            );
    }

    /**
     * Returns the full detail model for a single unit.
     * @param id - Unit ID.
     * @returns Observable emitting a {@link IUnitDetailsModel}.
     */
    public getById(id: string): Observable<IUnitDetailsModel> {
        return this._api
            .getById(id)
            .pipe(
                map((r) => new UnitDetailsModel(r.data)),
            );
    }

    /**
     * Creates a new unit.
     * @param payload - Unit creation data.
     * @returns Observable emitting the created unit ID.
     */
    public create(payload: ICreateUnitPayload): Observable<string> {
        return this._api.create(payload).pipe(map((r) => r.data));
    }

    /**
     * Updates an existing unit by its ID.
     * @param id - ID of the unit to update.
     * @param payload - Updated unit data.
     * @returns Observable emitting the updated unit ID.
     */
    public update(id: string, payload: IUpdateUnitPayload): Observable<string> {
        return this._api.update(id, payload).pipe(map((r) => r.data));
    }

    /**
     * Deletes a unit by its ID.
     * @param id - ID of the unit to delete.
     * @returns Observable emitting the deleted unit ID.
     */
    public delete(id: string): Observable<string> {
        return this._api.delete(id).pipe(map((r) => r.data));
    }

    /**
     * Returns the hierarchical unit tree rooted at the given unit ID.
     *
     * Consumed as a dropdown lookup (Energy Settings' Grid Billing Account and
     * Energy Meter Mappings unit pickers), where it is driven by a long-lived
     * facility stream via `switchMap`. A failure therefore **completes to an
     * empty tree** rather than erroring: the error interceptor has already
     * toasted it, and letting it propagate would kill the caller's outer stream
     * and freeze the dropdown for the rest of the session.
     * @param id - ID of the unit to root the tree at.
     * @returns Observable emitting an array of root-level {@link IUnitTreeModel}, or an empty array on failure.
     */
    public getTree(id: string): Observable<IUnitTreeModel[]> {
        return this._api
            .getTree(id)
            .pipe(
                map((r) => r.data.map((dto) => new UnitTreeModel(dto))),
                catchError(() => of<IUnitTreeModel[]>([])),
            );
    }

    /**
     * Returns every unit below a building as a flat list, in the backend's order.
     *
     * Unlike {@link getTree}, a failure is passed on to the caller: whether an
     * empty list or an error state is the right answer is the consumer's call.
     * @param buildingId - ID of the building whose units to list.
     * @returns Observable emitting an array of {@link IUnitListItemModel}.
     */
    public getList(buildingId: string): Observable<IUnitListItemModel[]> {
        return this._api
            .getList(buildingId)
            .pipe(
                map((response) => (response.hasData ? response.data : [])),
                map((rows) => rows.map((dto) => new UnitListItemModel(dto))),
            );
    }

}
